/**
 * Parts — top-level browse of the product catalog. Read-only for
 * every role; edit affordances live in Settings → Catalog (where the
 * existing tab handles add/edit).
 *
 * Visibility (server-side via /api/catalog → list_catalog_for_role):
 *   - platform  — all items; vendor filter dropdown narrows.
 *   - vendor    — items the vendor supplies (mttr.item.company_id).
 *   - customer  — items supplied by vendors associated with the
 *                 customer's smartboxes (via md_smartbox_vendor).
 *
 * Same data + paging shape as the Settings → Catalog tab; this page
 * just gives it a top-level browse URL with no admin chrome.
 */
import React, { useCallback, useEffect, useState } from "react";

import { Button } from "../components/ui/button";
import CreatePartForm from "../components/catalog/CreatePartForm";
import PartsImportModal from "../components/catalog/PartsImportModal";
import { useAuth } from "../contexts/AuthContext";
import {
  type CatalogItem,
  type CatalogPage,
  type VendorOption,
  fetchAllCatalog,
  fetchCatalog,
  fetchCatalogVendors,
  setCatalogItemActive,
} from "../services/catalogApi";

const PAGE_SIZE = 100;

const fmtMoney = (n: number | null): string =>
  n == null ? "—" : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const PartsPage: React.FC = () => {
  const { token, role, can } = useAuth();
  // Unit price is hidden from users without pricing.view (the server also
  // nulls unit_price for them). Hide the whole column so layout stays clean.
  const showPricing = can("pricing.view");
  // Vendors + platform can add new parts here; customers browse only.
  const canCreate = role === "platform" || role === "vendor";
  // Same audience may activate/deactivate (server re-checks, and restricts a
  // vendor to its own parts).
  const canToggleActive = canCreate;
  // Only platform users can SEE deactivated parts at all — the server ignores
  // include_inactive for everyone else, so don't offer the control to them.
  const canSeeInactive = role === "platform";
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState<CatalogPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [vendorFilter, setVendorFilter] = useState<number | "">("");
  const [offset, setOffset] = useState(0);

  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const p = await fetchCatalog(token, {
        q: q || undefined,
        vendor: vendorFilter === "" ? undefined : vendorFilter,
        includeInactive: canSeeInactive && showInactive,
        limit: PAGE_SIZE,
        offset,
      });
      setPage(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, q, vendorFilter, offset, canSeeInactive, showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    fetchCatalogVendors(token)
      .then(setVendors)
      .catch(() => setVendors([]));
  }, [token]);

  // Flip a part's active flag. Updates just that row in place rather than
  // refetching — except when an inactive row would drop out of the current
  // view, which needs a reload so pagination totals stay right.
  const onToggleActive = useCallback(
    async (item: CatalogItem): Promise<void> => {
      if (!token) return;
      const next = !item.is_active;
      try {
        const updated = await setCatalogItemActive(token, item.item_id, next);
        if (!next && !(canSeeInactive && showInactive)) {
          void load();
          return;
        }
        setPage((prev) =>
          prev === null
            ? prev
            : {
                ...prev,
                items: prev.items.map((r) =>
                  r.item_id === updated.item_id ? updated : r,
                ),
              },
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [token, canSeeInactive, showInactive, load],
  );

  const onSearchSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setOffset(0);
    setQ(qInput.trim());
  };

  // ---------- CSV utilities (export + template) ----------

  const CSV_HEADERS = ["sku","name","vendor","oem_no","category","unit_price","lead_time_days"] as const;

  const csvEscape = (v: unknown): string => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const downloadBlob = (name: string, body: string): void => {
    const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isoStamp = (): string => new Date().toISOString().slice(0, 10);

  const onDownloadTemplate = (): void => {
    // Blank template plus one example row so the user can see the
    // expected shape without inventing all the columns from scratch.
    const example = ["EXAMPLE-SKU","Example seal, 3/4in","Acme Filters",
                     "OEM-0740","Seals","12.34","7"];
    const body = [CSV_HEADERS.join(","), example.map(csvEscape).join(",")].join("\n");
    downloadBlob(`parts-import-template.csv`, body);
  };

  const onExportCsv = async (): Promise<void> => {
    if (!token) return;
    setExporting(true);
    try {
      const items = await fetchAllCatalog(token, {
        q: q || undefined,
        vendor: vendorFilter === "" ? undefined : Number(vendorFilter),
      });
      const rows = items.map((it) => [
        it.sku ?? "",
        it.name ?? "",
        it.company_name ?? "",
        it.oem_no ?? "",
        it.category_name ?? "",
        it.unit_price ?? "",
        it.lead_time_days ?? 0,
      ]);
      const body = [
        CSV_HEADERS.join(","),
        ...rows.map((r) => r.map(csvEscape).join(",")),
      ].join("\n");
      downloadBlob(`parts-${isoStamp()}.csv`, body);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  // Vendor dropdown is useful when the user can see MORE than one
  // vendor (platform users; customers with multiple vendor mappings).
  // For single-vendor users (vendor role; customers with one vendor)
  // the dropdown would only ever show one option besides "All", so
  // hide it.
  const showVendorPicker = vendors.length > 1;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <header className="mb-4">
        <h1 className="text-xl font-bold">Parts List</h1>
        <p className="text-xs text-secondary-500 mt-1">
          {role === "customer" &&
            "Parts available from vendors associated with your smartboxes."}
          {role === "vendor" && "Parts your company supplies."}
          {role === "platform" && "Every product across the platform."}
        </p>
      </header>

      {/* Filter bar */}
      <form
        onSubmit={onSearchSubmit}
        className="flex flex-wrap items-end gap-2 bg-secondary-50 border border-secondary-200 rounded-md p-3 mb-3"
      >
        <label className="text-xs text-secondary-600 flex-1 min-w-[200px]">
          <div>Search SKU / name / OEM #</div>
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="e.g. valve, plunger, 0740"
            className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
          />
        </label>
        <Button type="submit" size="sm">
          Search
        </Button>
        {showVendorPicker && (
          <label className="text-xs text-secondary-600">
            <div>Vendor</div>
            <select
              value={vendorFilter === "" ? "" : String(vendorFilter)}
              onChange={(e) => {
                setOffset(0);
                setVendorFilter(
                  e.target.value === "" ? "" : Number(e.target.value),
                );
              }}
              className="border border-secondary-300 rounded px-2 py-1 text-sm bg-white min-w-[200px]"
            >
              <option value="">— All vendors —</option>
              {vendors.map((v) => (
                <option key={v.company_id} value={v.company_id}>
                  {v.company_name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="flex gap-1.5 ml-auto flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void onExportCsv()}
            disabled={exporting}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          {canCreate && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onDownloadTemplate}
              >
                Template
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowImport(true)}
              >
                Import CSV
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => setShowCreate((v) => !v)}
                variant={showCreate ? "outline" : "default"}
              >
                {showCreate ? "Cancel" : "Add part"}
              </Button>
            </>
          )}
        </div>
      </form>

      <PartsImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => {
          // Refresh the visible page so imported / updated rows appear.
          setOffset(0);
          void load();
        }}
      />

      {showCreate && canCreate && (
        <div className="mb-3">
          <CreatePartForm
            vendors={vendors}
            onCreated={() => {
              // Refresh from the server so the new part shows up with
              // whatever server-side derivations (id, timestamps, lead
              // time defaults) landed. Cheaper than tracking one insert.
              setShowCreate(false);
              setOffset(0);
              void load();
            }}
            onCancel={() => setShowCreate(false)}
          />
        </div>
      )}

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded mb-3">
          {error}
        </div>
      )}

      {/* Header + pagination */}
      {page && (
        <div className="flex items-center justify-between text-xs text-secondary-600 mb-2">
          <span>
            <strong>{page.total.toLocaleString()}</strong> part
            {page.total === 1 ? "" : "s"}
            {q && <> matching "{q}"</>}
            {page.total > 0 && (
              <>
                {" "}
                — showing {offset + 1}–
                {Math.min(offset + page.items.length, page.total)}
              </>
            )}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || loading}
            >
              ← Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= page.total || loading}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      {canSeeInactive && (
        <label className="flex items-center gap-2 text-xs text-secondary-600 mb-2 select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => {
              setOffset(0);
              setShowInactive(e.target.checked);
            }}
          />
          Show deactivated parts
          <span className="text-secondary-400">
            (hidden from Match Tags and from customer/vendor lists)
          </span>
        </label>
      )}

      {/* Table */}
      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-secondary-50 text-secondary-700 uppercase">
            <tr>
              <th className="text-left px-3 py-2">SKU / Name</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Category</th>
              {showPricing && <th className="text-right px-3 py-2">Unit price</th>}
              <th className="text-right px-3 py-2">Lead time</th>
              {canToggleActive && <th className="text-right px-3 py-2">Status</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {loading && !page ? (
              <tr>
                <td colSpan={(canToggleActive ? 6 : 5) - (showPricing ? 0 : 1)} className="px-3 py-6 text-center text-secondary-500">
                  loading…
                </td>
              </tr>
            ) : page && page.items.length === 0 ? (
              <tr>
                <td colSpan={(canToggleActive ? 6 : 5) - (showPricing ? 0 : 1)} className="px-3 py-6 text-center text-secondary-500">
                  No parts match those filters.
                </td>
              </tr>
            ) : (
              page?.items.map((item) => (
                <PartRow
                  key={item.item_id}
                  item={item}
                  canToggleActive={canToggleActive}
                  showPricing={showPricing}
                  onToggleActive={onToggleActive}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PartsPage;

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

const PartRow: React.FC<{
  item: CatalogItem;
  canToggleActive: boolean;
  showPricing: boolean;
  onToggleActive: (item: CatalogItem) => void | Promise<void>;
}> = ({ item, canToggleActive, showPricing, onToggleActive }) => (
  <tr
    className={`hover:bg-secondary-50 align-top ${
      item.is_active ? "" : "bg-secondary-50/60 text-secondary-400"
    }`}
  >
    <td className="px-3 py-2">
      <div className="font-mono font-semibold">
        {item.sku ?? "—"}
        {!item.is_active && (
          <span className="ml-2 font-sans font-normal text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary-200 text-secondary-700">
            inactive
          </span>
        )}
      </div>
      {item.name && (
        <div className="text-secondary-500 text-[11px]">{item.name}</div>
      )}
      {item.oem_no && (
        <div className="text-secondary-400 text-[10px]">OEM: {item.oem_no}</div>
      )}
    </td>
    <td className="px-3 py-2 text-secondary-600">
      {item.company_name ?? "—"}
    </td>
    <td className="px-3 py-2 text-secondary-600">
      {item.category_name ?? "—"}
    </td>
    {showPricing && (
      <td className="px-3 py-2 text-right font-mono">
        {fmtMoney(item.unit_price)}
      </td>
    )}
    <td className="px-3 py-2 text-right font-mono">{item.lead_time_days}d</td>
    {canToggleActive && (
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => void onToggleActive(item)}
          title={
            item.is_active
              ? "Hide this part from Match Tags and from customer/vendor parts lists"
              : "Make this part selectable again"
          }
        >
          {item.is_active ? "Deactivate" : "Reactivate"}
        </Button>
      </td>
    )}
  </tr>
);
